import { useEffect, useState } from 'react';
import {useDispatch, useSelector} from 'react-redux';
import { GetEvidencesList, GetFinesTrafficDetails } from '../../../../../reduxToolkit/Traffic/TrafficApi';
import {errorMsg, tryGetAddress} from '../../../../../Apis/functions';
import {permissionObj} from "../../../../../Helpers/permission";
import {ACTION_MODEL} from "../../../../../Helpers/ActionModel";
import {STATUS_MODEL} from "../../../../../enums/statusEnum";
import {GEOFENCES_MODEL} from '../../Manage/Model/Model';
import {debounce} from 'lodash';

/**
 * @Email : sgarcia@ibl.mx
 * @Author: Susana Garcia Rodriguez
 * @jobTitle: Frontend Developer
 */

/**
 * TrafficDetailsModalApp
 *
 * Hook-like component that fetches and manages traffic fine details for a selected item.
 * It handles fetching coordinates, address resolution, evidences, and prepares data for a modal view.
 *
 * @component
 * @param {Object} props - Component props
 * @param {string|number} props.itemSelected - ID of the selected traffic fine
 *
 * @returns {Object} Object containing state and functions for use in a traffic details modal
 * @property {Array<number>} latLng - Latitude and longitude array of the fine
 * @property {Function} setLatitude - Setter for latitude
 * @property {Function} setLongitude - Setter for longitude
 * @property {Array} dataEvidences - List of evidences for the fine
 * @property {string} direction - Resolved address for the fine coordinates
 * @property {Object|null} trafficData - Detailed traffic fine data
 * @property {Function} buildEvidenceImages - Helper function to construct full URLs of evidence images
 */
const TrafficDetailsModalApp = ({ itemSelected, optionModal, onHide,  setShowConfirmationPay }) => {
    const dispatch = useDispatch();
    const DEV = useSelector((state) => state?.CommonsSlice?.development);
    const {permission} = useSelector((state) => state.authenticatioauthennSlice);
    const [trafficData, setTrafficData] = useState(null);
    const [latLng, setLatLng] = useState(null);
    const [dataEvidences, setDataEvidences] = useState();
    const [direction, setDirection] = useState();
    const [paymentOptionsView, setPaymentOptionsView] = useState(false);
    const paymentPermission = DEV || permission?.includes(permissionObj?.WEB_TRAFFIC_USERS_MANAGE_PAYMENT);
    const hideButtonsModal = optionModal === ACTION_MODEL.DETAILS || (!paymentPermission && STATUS_MODEL.PAID !== parseInt(trafficData?.statusId)) || paymentOptionsView;
    const [geofence, setGeofence] = useState(null);
    const [center, setCenter] = useState(null);
    const [locations, setLocations] = useState([]);
    /**
     * Fetch traffic fine details and evidences whenever `itemSelected` changes.
     * Updates state for coordinates, direction, trafficData, and evidences.
     */
    useEffect(() => {
        if (!itemSelected) return;

        // Fetch traffic fine details
        dispatch(GetFinesTrafficDetails(itemSelected)).then(
            ({
                 payload: {
                     data: { data, success, code },
                 },
             }) => {
                if (success) {
                    const aux = {...GEOFENCES_MODEL.toSetMap(data?.geofence)};
                    setGeofence(aux);
                    let center = GEOFENCES_MODEL.getGeofenceCenter(aux);
                    if(center) {
                        setCenter(center);
                    }
                    setLocations([{position: [data.latitude, data.longitude]}])
                    setTrafficData(data);
                    setLatLng([data.latitude, data.longitude]);
                    handleMapMove(data.latitude, data.longitude);
                } else errorMsg(code);
            },
        );

        // Fetch evidences for the traffic fine
        dispatch(GetEvidencesList(itemSelected)).then(({ payload }) => {
            const { data, success, code } = payload.data;
            if (success) setDataEvidences(data);
            else errorMsg(code);
        });
    }, [itemSelected]);

    const handleMapMove = debounce((lat, lng) => {
        tryGetAddress(lat, lng, dispatch, setDirection);
    }, 800);

    /**
     * Build full URLs for evidence images
     * @param {Array} dataEvidences - List of evidence objects
     * @param {string} baseUrl - Base URL to prepend to each evidence file path
     * @returns {Array<string>} List of full image URLs
     */
    const buildEvidenceImages = (dataEvidences = [], baseUrl = '') => {
        if (!Array.isArray(dataEvidences)) return [];
        return dataEvidences
            .map((item) => {
                const path = item?.fileUploadDto?.filePath;
                return path ? `${baseUrl}${path}` : null;
            })
            .filter(Boolean);
    };

    /**
     * Handles the confirmation action when the user confirms a traffic fine.
     * If the fine's status is "PAID", it hides the current modal and shows a confirmation message.
     * Otherwise, if the user has payment permissions, it opens the payment options view.
    **/
    const onConfirm = () => {
        if(STATUS_MODEL.PAID === parseInt(trafficData.statusId)){
            onHide();
            setShowConfirmationPay(true);
        }else if(paymentPermission)
            setPaymentOptionsView(true);
    }

    return {
        latLng,
        dataEvidences,
        direction,
        trafficData,
        buildEvidenceImages,
        paymentOptionsView,
        setPaymentOptionsView,
        hideButtonsModal,
        onConfirm,
        geofence,
        center,
        locations, setLocations,
        handleMapMove
    };
};

export default TrafficDetailsModalApp;
